import AuthFlowFrame from "../../components/AuthFlowFrame";
import AccountMappingDropdown from "../AccountMappingDropdown";
import { Content } from "@jobber/components/Content";
import { Text } from "@jobber/components/Text";
import { Emphasis } from "@jobber/components/Emphasis";
import { Link } from "@jobber/components/Link";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getXeroAccounts, saveXeroAccounts } from "../../services";
import { AuthFlowButton } from "../../types";
import { Button, Divider, Flex, Grid, Heading, Icon } from "@jobber/components";
import { useBreakpoints } from "@jobber/hooks/useBreakpoints";
import { showError } from "../../helpers";
import jobberLogo from "assets/images/jobber_logo.svg";
import xeroLogo from "assets/images/xero_logo.svg";

type AccountMappingContentProps = {
    nextStepLink: string,
};

function AccountMappingContent(props: AccountMappingContentProps) {
    const [ledgerAccount, setLedgerAccount] = useState("");
    const [bankAccount, setBankAccount] = useState("");

    const [loadingLedgerAccountOptions, setLoadingLedgerAccountOptions] = useState(true);
    const [loadingBankAccountOptions, setLoadingBankAccountOptions] = useState(true);

    const [ledgerAccountOptions, setLedgerAccountOptions] = useState([]);
    const [bankAccountOptions, setBankAccountOptions] = useState([]);

    useEffect(() => {
        (async () => {
            const state = sessionStorage.getItem('state');

            try {
                const {data: ledgerAccounts} = await getXeroAccounts('ledger', state);
                setLedgerAccountOptions(ledgerAccounts)
                if (ledgerAccounts.length === 1) {
                    setLedgerAccount(ledgerAccounts[0].AccountID)
                }
            } catch(e: any) {
                showError("Unable to retrieve ledger accounts: " + e.message);
            }
            setLoadingLedgerAccountOptions(false);

            try {
                const {data: bankAccounts} = await getXeroAccounts('bank', state);
                setBankAccountOptions(bankAccounts);
                if (bankAccounts.length === 1) {
                    setBankAccount(bankAccounts[0].AccountID)
                }
            } catch(e: any) {
                showError("Unable to retrieve bank accounts: " + e.message);
            }
            setLoadingBankAccountOptions(false);
        })();
    }, []);

    const [accountSelectionsPending, setAccountSelectionsPending] = useState(true);
    useEffect(() => {
        setAccountSelectionsPending(ledgerAccount == "" || bankAccount == "");
    }, [ledgerAccount, bankAccount]);

    const [isSavingAccounts, setIsSavingAccounts] = useState(false);
    const [buttonText, setButtonText] = useState("Continue");
    const navigate = useNavigate();

    function saveAccounts(e: React.MouseEvent) {
        e.preventDefault();
        setIsSavingAccounts(true);
        setButtonText("Connecting...");
        (async () => {
            try {
                const state = sessionStorage.getItem('state');
                await saveXeroAccounts(ledgerAccount, bankAccount, state);
                navigate(props.nextStepLink, {replace: true});
            } catch(e: any) {
                showError("Error saving accounts: " + e.message);
            }
        })();
    }

    const button: AuthFlowButton = {
        link: "/finish-setup", // this is only here for the user's sake, it's never followed directly
        text: buttonText,
        type: "secondary",
        disabled: accountSelectionsPending,
        loading: isSavingAccounts,
        onClick: saveAccounts,
    }

    let mediumAndUp = useBreakpoints().mediumAndUp;
    let jobberLogoEl =
        <img
            src={jobberLogo}
            alt="Jobber logo"
            style={{width: "25px", scale: "0.8", filter: "invert(1)"}}
        />
    let xeroLogoEl =
        <img
            src={xeroLogo}
            alt="Xero logo"
            style={{width: "25px"}}
        />

    return (
        <AuthFlowFrame
            pretitle={"Let's get you set up"}
            title={"Select which Xero accounts to map to"}
            step={2}
            totalSteps={3}
            button={button}
        >
            <Content type={"div"} spacing={"large"}>
                <Text size={"large"}>
                    Select the Xero accounts that Jobber invoice and payment data should map to:
                </Text>

                <Content type={"div"} spacing={"base"}>
                    {
                        mediumAndUp &&
                        <Grid alignItems={"center"}>
                            <Grid.Cell size={{md: 4, xs: 12}}>
                                <Flex gap={"small"} template={["shrink", "grow"]}>
                                    {jobberLogoEl}
                                    <Heading level={4}>Jobber</Heading>
                                </Flex>
                            </Grid.Cell>
                            <Grid.Cell size={{md: 8, xs: 12}}>
                                <Flex gap={"small"} template={["shrink", "grow"]}>
                                    {xeroLogoEl}
                                    <Heading level={4}>Xero</Heading>
                                </Flex>
                            </Grid.Cell>
                            <Grid.Cell size={{xs: 12}}>
                                <Divider/>
                            </Grid.Cell>
                        </Grid>
                    }
                    <Grid alignItems={"center"}>
                        <Grid.Cell size={{md: 4, xs: 12}}>
                            <Flex gap={"small"} template={["shrink", "grow"]}>
                                {!mediumAndUp && jobberLogoEl}
                                <Emphasis variation={"bold"}>
                                    Jobber sales (invoices)
                                </Emphasis>
                            </Flex>
                        </Grid.Cell>
                        <Grid.Cell size={{md: 8, xs: 12}}>
                            <Flex
                                gap={"small"}
                                template={mediumAndUp ? ["grow", "shrink"] : ["shrink", "grow"]}
                            >
                                {!mediumAndUp && xeroLogoEl }
                                <AccountMappingDropdown
                                    loadingAccountOptions={loadingLedgerAccountOptions}
                                    accountOptions={ledgerAccountOptions}
                                    setAccountCallback={setLedgerAccount}
                                ></AccountMappingDropdown>
                            </Flex>
                        </Grid.Cell>

                        <Grid.Cell size={{xs: 12}}>
                            <Divider/>
                        </Grid.Cell>

                        <Grid.Cell size={{md: 4, xs: 12}}>
                            <Flex gap={"small"} template={["shrink", "grow"]}>
                                {!mediumAndUp && jobberLogoEl}
                                <Emphasis variation={"bold"}>
                                    Payments received
                                </Emphasis>
                            </Flex>
                        </Grid.Cell>
                        <Grid.Cell size={{md: 8, xs: 12}}>
                            <Flex
                                gap={"small"}
                                template={mediumAndUp ? ["grow", "shrink"] : ["shrink", "grow"]}
                            >
                                {!mediumAndUp && xeroLogoEl}
                                <AccountMappingDropdown
                                    loadingAccountOptions={loadingBankAccountOptions}
                                    accountOptions={bankAccountOptions}
                                    setAccountCallback={setBankAccount}
                                ></AccountMappingDropdown>
                            </Flex>
                        </Grid.Cell>
                    </Grid>
                </Content>

                <Flex template={mediumAndUp ? ["grow", "shrink"] : ["grow"]}>
                    <Flex template={["shrink", "grow"]}>
                        <Icon name={"help"} color={"informative"} />
                        <Text size={"base"} variation={"subdued"}>
                            If you would like to create a new clearing account for reconciling payments in Xero, <Link url={"https://central.xero.com/s/article/Add-a-bank-account-or-credit-card-account"} external={true}>follow these instructions</Link>. Then click the “Refresh” button to the right to see your updated list of bank accounts in the dropdown above.
                        </Text>
                    </Flex>
                    <Button
                        icon={"redo"}
                        label={"Refresh"}
                        type={"tertiary"}
                        onClick={() => window.location.reload()}
                    />
                </Flex>
            </Content>
        </AuthFlowFrame>
    );
}

export default AccountMappingContent;
